import * as yup from 'yup'
import i18n from '../i18n'

export const email = yup
  .object({
    email: yup
      .string()
      .email(() => i18n.t('shared.errors.emailInvalid'))
      .required(() => i18n.t('shared.errors.emailRequired')),
  })
  .required()

export const loginSchema = yup
  .object({
    email: yup
      .string()
      .email(() => i18n.t('shared.errors.emailInvalid'))
      .max(50, () => i18n.t('shared.errors.emailTooLong'))
      .required(() => i18n.t('shared.errors.emailRequired')),
    password: yup
      .string()
      .min(8, () => i18n.t('shared.errors.passwordTooShort'))
      .max(50, () => i18n.t('shared.errors.emailTooLong'))
      .required(() => i18n.t('shared.errors.passwordRequired')),
    keepMeLogged: yup.boolean(),
  })
  .required()

// prettier-ignore
// eslint-disable-next-line no-useless-escape
const phoneRegExp = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im
const adeliFinessNumberRegExp = /^[0-9]{9}$/
const rppsNumberRegExp = /^[0-9]{11}$/
const ssnRegExp = /^[12]\d{2}(0[1-9]|1[0-2])\d{10}$/

export const RegistrationSchema = yup
  .object({
    companyName: yup
      .string()
      .min(1, () => i18n.t('shared.errors.nameTooShort'))
      .max(200, () => i18n.t('shared.errors.nameTooLong'))
      .required(() => i18n.t('shared.errors.companyNameRequired')),
    organizationId: yup.string().nullable().optional(),
    firstName: yup
      .string()
      .min(1, () => i18n.t('shared.errors.nameTooShort'))
      .max(30, () => i18n.t('shared.errors.nameTooLong'))
      .required(() => i18n.t('shared.errors.firstNameRequired')),
    lastName: yup
      .string()
      .min(1, () => i18n.t('shared.errors.nameTooShort'))
      .max(30, () => i18n.t('shared.errors.nameTooLong'))
      .required(() => i18n.t('shared.errors.lastNameRequired')),
    phoneNumber: yup
      .string()
      .min(7, () => i18n.t('shared.errors.phoneTooShort'))
      .max(15, () => i18n.t('shared.errors.phoneTooLong'))
      .required(() => i18n.t('shared.errors.phoneRequired'))
      .matches(phoneRegExp, () => i18n.t('shared.errors.phoneInvalid')),
    specialtyId: yup
      .number()
      .required(() => i18n.t('shared.errors.specialtyRequired')),
    email: yup
      .string()
      .email(() => i18n.t('shared.errors.emailInvalid'))
      .max(50, () => i18n.t('shared.errors.emailTooLong'))
      .required(() => i18n.t('shared.errors.emailRequired')),
    password: yup
      .string()
      .min(8, () => i18n.t('shared.errors.passwordTooShort'))
      .matches(/[A-Z]/, () => i18n.t('shared.errors.passwordUppercase'))
      .matches(/[0-9]/, () => i18n.t('shared.errors.passwordNumber'))
      .max(50, () => i18n.t('shared.errors.emailTooLong'))
      .required(() => i18n.t('shared.errors.passwordRequired')),
    confirmPassword: yup
      .string()
      .test(
        'passwords-match',
        () => i18n.t('shared.errors.passwordsMatch'),
        function (value) {
          return this.parent.password === value
        }
      )
      .required(() => i18n.t('shared.errors.confirmPasswordRequired')),
  })
  .required()

const doctorReferenceNumber = {
  adeliFinessNumber: yup
    .string()
    .matches(adeliFinessNumberRegExp, () =>
      i18n.t('shared.errors.adeliNumberFormat')
    ),
  rppsNumber: yup
    .string()
    .matches(rppsNumberRegExp, () => i18n.t('shared.errors.rppsNumberFormat')),
}

export const MyProfile = yup
  .object({
    companyName: yup
      .string()
      .min(1, () => i18n.t('shared.errors.nameTooShort'))
      .max(200, () => i18n.t('shared.errors.nameTooLong'))
      .required(() => i18n.t('shared.errors.companyNameRequired')),
    organizationId: yup.string().nullable().optional(),
    firstName: yup
      .string()
      .min(1, () => i18n.t('shared.errors.nameTooShort'))
      .max(30, () => i18n.t('shared.errors.nameTooLong'))
      .required(() => i18n.t('shared.errors.firstNameRequired')),
    lastName: yup
      .string()
      .min(1, () => i18n.t('shared.errors.nameTooShort'))
      .max(30, () => i18n.t('shared.errors.nameTooLong'))
      .required(() => i18n.t('shared.errors.lastNameRequired')),
    phoneNumber: yup
      .string()
      .min(7, () => i18n.t('shared.errors.phoneTooShort'))
      .max(15, () => i18n.t('shared.errors.phoneTooLong'))
      .required(() => i18n.t('shared.errors.phoneRequired'))
      .matches(phoneRegExp, () => i18n.t('shared.errors.phoneInvalid')),
    email: yup
      .string()
      .email(() => i18n.t('shared.errors.emailInvalid'))
      .max(50, () => i18n.t('shared.errors.emailTooLong'))
      .required(() => i18n.t('shared.errors.emailRequired')),
    specialtyId: yup
      .number()
      .required(() => i18n.t('shared.errors.specialtyRequired')),
    avatar: yup
      .mixed()
      .test('fileSize', 'The file is too large', (value: any) => {
        if (value) {
          if (!value.length) return true // attachment is optional
          return value[0].size <= 2000000
        }
      }),
    adeliFinessNumberRegExp: doctorReferenceNumber.adeliFinessNumber.optional(),
    rppsNumberRegExp: doctorReferenceNumber.rppsNumber.optional(),
  })
  .required()

export const NewPatient = yup
  .object({
    firstName: yup
      .string()
      .min(2, () => i18n.t('shared.errors.firstNameTooShort'))
      .max(75, () => i18n.t('shared.errors.firstNameTooLong'))
      .required(() => i18n.t('shared.errors.firstNameRequired')),

    lastName: yup
      .string()
      .min(2, () => i18n.t('shared.errors.lastNameTooShort'))
      .max(75, () => i18n.t('shared.errors.lastNameTooLong'))
      .required(() => i18n.t('shared.errors.lastNameRequired')),

    birthName: yup
      .string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .min(2, () => i18n.t('shared.errors.birthNameTooShort'))
      .max(75, () => i18n.t('shared.errors.birthNameTooLong'))
      .optional(),

    dateOfBirth: yup
      .date()
      .max(new Date(), () => i18n.t('shared.errors.dateOfBirthFuture'))
      .required(() => i18n.t('shared.errors.dateOfBirthRequired')),

    gender: yup
      .string()
      .required(() => i18n.t('shared.errors.genderRequired'))
      .oneOf(['male', 'female'], () => i18n.t('shared.errors.genderInvalid')),

    address: yup
      .string()
      .min(2, () => i18n.t('shared.errors.addressTooShort'))
      .max(250, () => i18n.t('shared.errors.addressTooLong'))
      .required(() => i18n.t('shared.errors.addressRequired')),

    socialSecurityNumber: yup
      .string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .matches(ssnRegExp, () => i18n.t('shared.errors.ssnInvalid'))
      .length(15, () => i18n.t('shared.errors.ssnLength'))
      .optional(),

    twinRank: yup
      .number()
      .nullable()
      .transform((value) => {
        if (value === '' || value === null || isNaN(value)) return null
        return Number(value)
      })
      .oneOf([1, 2, 3, 4], () => i18n.t('shared.errors.twinRankInvalid'))
      .optional(),

    phoneNumber: yup
      .string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .matches(phoneRegExp, () => i18n.t('shared.errors.phoneInvalid'))
      .optional(),

    confirmedPatientConsent: yup
      .boolean()
      .required(() => i18n.t('shared.errors.patientConsentRequired'))
      .oneOf([true], () => i18n.t('shared.errors.patientConsentConfirmed')),

    fullAddress: yup.string().nullable().optional(),

    location: yup
      .object({
        type: yup.string().equals(['Point']),
        coordinates: yup.array().of(yup.number()).length(2),
      })
      .nullable()
      .optional(),
  })
  .required()

export const NewRequest = yup
  .object({
    patientId: yup.mixed<string | number>().nullable().defined(),
    title: yup
      .string()
      .min(2, () => i18n.t('shared.errors.titleTooShort'))
      .max(150, () => i18n.t('shared.errors.titleTooLong'))
      .required(() => i18n.t('shared.errors.titleRequired')),
    description: yup
      .string()
      .min(2, () => i18n.t('shared.errors.descriptionTooShort'))
      .max(5000, () => i18n.t('shared.errors.descriptionTooLong'))
      .required(() => i18n.t('shared.errors.descriptionRequired')),
    category: yup
      .string()
      .min(2, () => i18n.t('shared.errors.categoryTooShort'))
      .max(75, () => i18n.t('shared.errors.categoryTooLong'))
      .required(() => i18n.t('shared.errors.categoryRequired')),
    tagIds: yup
      .array()
      .of(yup.number().defined())
      .min(1, () => i18n.t('shared.errors.tagRequired'))
      .max(5, () => i18n.t('shared.errors.maxTagsAllowed'))
      .required(() => i18n.t('shared.errors.tagsRequired')),
    files: yup
      .array()
      .of(yup.mixed<File>().defined())
      .min(1, () => i18n.t('shared.errors.imageRequired'))
      .required(() => i18n.t('shared.errors.imagesRequired')),
    receiverEmail: yup
      .string()
      .email(() => i18n.t('shared.errors.emailInvalid'))
      .required(() => i18n.t('shared.errors.doctorRequired')),
    openWhiteBoard: yup.boolean().required(),
  })
  .required()

export const CloseRequestDoctorNumbers = yup
  .object({
    adeliFinessNumber: doctorReferenceNumber.adeliFinessNumber.required(),
    rppsNumber: doctorReferenceNumber.rppsNumber.required(),
  })
  .required()
